import { BogoStickyFooterContent } from '@mc/bogo'
import { useBogo } from '@mc/bogo-utils'
import { Conditional, ShowIf } from '@mc/conditional-rendering'
import { FreeTrialStickyFooter, useFreeTrial } from '@mc/free-trial'
import { usePromotion } from '@mc/promo-utils'
import { PromoStickyFooterContent } from '@mc/promos'

import CTA from 'components/CTA'
import { trackPropsType } from 'components/CTA/events'
import { PricingText } from 'components/PricingText'
import {
  usePersonalizedStickyFooterExp,
  PersonalizedStickyFooterContent,
} from 'experiments/pen-5361_v1_personalized_sticky_footer'
import {
  CMTimelineViewStickyFooter,
  useCMTimelineViewExp,
} from 'experiments/pen-5613_v1_cm_timeline_view'

interface CTAProps {
  href?: string
  trackProps: trackPropsType
  onClick: (action: string) => void
  children: React.ReactNode
  fullWidth?: boolean
  className?: string
}

const HighlightedLayout = ({ children }: { children: React.ReactNode }) => (
  <div style={{ backgroundColor: 'var(--mc-color-pink-500)' }}>{children}</div>
)

export const StickyContent = ({ ctaProps }: { ctaProps: CTAProps }) => {
  const { isBogoOfferEligible } = useBogo()
  const { isPromotionOn } = usePromotion()
  const { trial } = useFreeTrial()
  const { isVariant: isPersonalizedStickyFooterVariant } =
    usePersonalizedStickyFooterExp()
  const { isVariant1: isCMTimelineViewVariant1 } = useCMTimelineViewExp()
  const isHighlightedFooter =
    isPromotionOn || isBogoOfferEligible || isPersonalizedStickyFooterVariant

  const renderSignUpCTA = (signUpCtaProps?: Partial<CTAProps>) => (
    <CTA
      href={ctaProps.href}
      trackProps={ctaProps.trackProps}
      isBasic
      onClick={ctaProps.onClick}
      kind={isHighlightedFooter ? 'play' : undefined}
      {...signUpCtaProps}
    >
      {ctaProps.children}
    </CTA>
  )

  return (
    <Conditional>
      <ShowIf condition={!!trial.eligible}>
        <FreeTrialStickyFooter />
      </ShowIf>

      <ShowIf condition={isPromotionOn}>
        <HighlightedLayout>
          <PromoStickyFooterContent
            SignUpCta={renderSignUpCTA}
            PricingText={() => <PricingText isStickyFooter />}
          />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf condition={isBogoOfferEligible}>
        <HighlightedLayout>
          <BogoStickyFooterContent
            SignUpCta={renderSignUpCTA}
            PricingText={() => <PricingText isStickyFooter />}
          />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf condition={isCMTimelineViewVariant1}>
        <CMTimelineViewStickyFooter />
      </ShowIf>

      <ShowIf condition={isPersonalizedStickyFooterVariant}>
        <HighlightedLayout>
          <PersonalizedStickyFooterContent SignUpCta={renderSignUpCTA} />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf isDefault>
        <div className='container d-flex align-items-center justify-content-end mc-py-4'>
          <p className='mc-text-h7 mc-opacity--hinted mc-mr-4'>
            <PricingText isStickyFooter isStickyFooterMoneyBackExperiment />
          </p>
          {renderSignUpCTA()}
        </div>
      </ShowIf>
    </Conditional>
  )
}
